import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ChatModal from '../components/ChatModal';
import Layout from '../components/Layout';
import ModalPortal from '../components/ModalPortal';
import RepairNotesPanel from '../components/RepairNotesPanel';
import QRCode from 'react-qr-code';
import MagasinBadge from '../components/MagasinBadge';
import PostSaveRepairModal from '../components/repairs/PostSaveRepairModal';
import QrScanner from '../components/QrScanner';
import CreateDevisFromRepairModal from '../components/CreateDevisFromRepairModal';
import ReparationsAtelierView from '../features/reparations/ReparationsAtelierView';
import ReparationsDetailPanel from '../features/reparations/ReparationsDetailPanel';
import RepairBulkNoteModal from '../features/reparations/RepairBulkNoteModal';
import RepairViewErrorBoundary from '../components/RepairViewErrorBoundary';
import { getAllowedStatuses, repairStatusBadge } from '../features/reparations/reparationsUtils';
import { printTicket80mm } from '../utils/ticket80mmPrint';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  FileText,
  RefreshCw,
  Eye,
  Phone,
  User,
  Smartphone,
  Wrench,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Package,
  Printer,
  StickyNote,
  Globe,
  MapPin,
  Mail,
  Camera,
  QrCode,
  ScanLine,
  Receipt,
  Settings,
  X
} from 'lucide-react';

// Mappe les valeurs de statut backend (FR) vers des clés i18n pour l'AFFICHAGE
// uniquement. La logique continue d'utiliser la valeur brute ; repli sur la
// valeur brute pour les statuts personnalisés non connus.
const STATUS_LABEL_KEYS = {
  'En attente de dépôt': 'rep.status.awaitingDeposit',
  'Reçu': 'rep.status.received',
  'En Cours': 'rep.status.inProgress',
  'Attente Pièces': 'rep.status.awaitingParts',
  'Terminé': 'rep.status.completed',
  'Non Réparable': 'rep.status.notRepairable',
  'Livré': 'rep.status.delivered',
  'Refusé': 'rep.status.refused',
};

// Idem pour les modes de paiement : valeur backend conservée, libellé traduit.
const PAY_MODE_LABEL_KEYS = {
  'Espèces': 'rep.payCash',
  'CB': 'rep.payCard',
  'Virement': 'rep.payTransfer',
  'Chèque': 'rep.payCheck',
};

const ReparationsPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // PhaseB (2026-02) — Filtre magasin sur la page Réparations.
  // Pour les admin_magasin, le backend scope déjà les résultats au magasin
  // assigné (cf. C7) ; ce filtre frontend permet à super_admin/technicien
  // de filtrer visuellement parmi les magasins multiples.
  const [magasinFilter, setMagasinFilter] = useState('');
  const [magasinsList, setMagasinsList] = useState([]);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [acompteData, setAcompteData] = useState({ montant: 0, mode_paiement: 'Espèces' });
  const [showAcomptesListModal, setShowAcomptesListModal] = useState(false);
  const [acomptesList, setAcomptesList] = useState([]);
  const [chatContext, setChatContext] = useState(null);
  const [timelineDevice, setTimelineDevice] = useState(null);
  const [statusModalDevice, setStatusModalDevice] = useState(null);
  const [notesDeviceRef, setNotesDeviceRef] = useState(null);
  const [onlineRequests, setOnlineRequests] = useState([]);
  const [showOnlineRequests, setShowOnlineRequests] = useState(false);
  const [qrDevice, setQrDevice] = useState(null);
  const [postSaveModal, setPostSaveModal] = useState({ open: false, ref: null, mode: 'ticket' });
  const [showQrSearch, setShowQrSearch] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [qrSearchRef, setQrSearchRef] = useState('');
  const [createDevisDevice, setCreateDevisDevice] = useState(null);
  const [selectedListRef, setSelectedListRef] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [bulkSelectedRefs, setBulkSelectedRefs] = useState([]);
  const [showBulkNoteModal, setShowBulkNoteModal] = useState(false);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [newStatusName, setNewStatusName] = useState('');
  const [statuses, setStatuses] = useState(['En attente de dépôt', 'Reçu', 'En Cours', 'Attente Pièces', 'Terminé', 'Non Réparable', 'Livré']);

  const buildTrackUrl = (ref) => {
    return `${window.location.origin}/track/${ref}`;
  };

  // Libellés traduits pour l'affichage (la logique garde la valeur brute).
  const statusLabel = (s) => (s && STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : (s || ''));
  const payModeLabel = (m) => (m && PAY_MODE_LABEL_KEYS[m] ? t(PAY_MODE_LABEL_KEYS[m]) : (m || ''));

  const fetchOnlineRequests = async () => {
    try {
      const response = await api.get('/devices/online-requests');
      setOnlineRequests(response.data);
    } catch (error) {
      console.error('Error fetching online requests:', error);
    }
  };

  const confirmArrival = async (ref) => {
    if (!window.confirm(t('rep.confirmArrivalAsk', { ref }))) return;
    try {
      await api.put(`/devices/${ref}/confirm-arrival`);
      fetchOnlineRequests();
      fetchDevices();
      alert(t('rep.confirmArrivalDone'));
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.confirmArrivalError'));
    }
  };

  const openPdfForPrint = async (endpoint) => {
    try {
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (!printWindow) {
        alert(t('rep.allowPopups'));
        return;
      }
      const triggerPrint = () => {
        printWindow.focus();
        printWindow.print();
      };
      printWindow.addEventListener('load', triggerPrint);
      setTimeout(triggerPrint, 1200);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.downloadError'));
    }
  };

  const handleOpenPrint = (ref, mode = 'ticket') => {
    setPostSaveModal({ open: true, ref, mode });
  };

  const handlePrintAcompte = async (acompteId) => {
    try {
      await printTicket80mm(api, { type: 'acompte', ref: acompteId });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.printAcompteError'));
    }
  };

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) return;
    try {
      await api.post('/repair-statuses', { name: newStatusName.trim() });
      const res = await api.get('/repair-statuses');
      if (res.data?.statuses?.length) setStatuses(res.data.statuses);
      setNewStatusName('');
    } catch (err) {
      alert(err.response?.data?.detail || t('rep.error'));
    }
  };

  const handleDeleteStatus = async (name) => {
    if (!window.confirm(t('rep.deleteStatusAsk', { name }))) return;
    try {
      await api.delete(`/repair-statuses/${encodeURIComponent(name)}`);
      const res = await api.get('/repair-statuses');
      if (res.data?.statuses?.length) setStatuses(res.data.statuses);
    } catch (err) {
      alert(err.response?.data?.detail || t('rep.error'));
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchOnlineRequests();
  }, [statusFilter]);

  const fetchDevices = async () => {
    try {
      let params = '';
      if (statusFilter) {
        params = `?status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await api.get(`/devices${params}`);
      const list = Array.isArray(response.data) ? response.data : (response.data?.devices || []);
      setDevices(list);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (ref, newStatus) => {
    try {
      await api.put(`/devices/${ref}/status?status=${encodeURIComponent(newStatus)}`);
      fetchDevices();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.updateError'));
    }
  };

  const handleDelete = async (ref) => {
    if (!window.confirm(t('rep.deleteRecordAsk'))) return;
    try {
      await api.delete(`/devices/${ref}`);
      fetchDevices();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.deleteError'));
    }
  };

  const handleAddAcompte = async () => {
    if (acompteData.montant <= 0) {
      alert(t('rep.invalidAmount'));
      return;
    }

    try {
      await api.post('/acomptes', {
        device_ref: selectedDevice.ref,
        montant: acompteData.montant,
        mode_paiement: acompteData.mode_paiement
      });
      setShowAcompteModal(false);
      setAcompteData({ montant: 0, mode_paiement: 'Espèces' });
      fetchDevices();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('rep.addError'));
    }
  };

  const fetchAcomptes = async (ref) => {
    try {
      const response = await api.get(`/acomptes?device_ref=${ref}`);
      setAcomptesList(response.data);
      setShowAcomptesListModal(true);
    } catch (error) {
      console.error('Error fetching acomptes:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'En attente de dépôt': 'badge-attente-depot',
      'Reçu': 'badge-recu',
      'En Cours': 'badge-en-cours',
      'Attente Pièces': 'badge-attente',
      'Terminé': 'badge-termine',
      'Livré': 'badge-livre',
      'Non Réparable': 'badge-non-reparable'
    };
    return <span className={`badge-status ${badges[status] || 'badge-recu'}`}>{status}</span>;
  };

  // Fetch custom statuses
  useEffect(() => {
    api.get('/repair-statuses').then(res => {
      if (res.data?.statuses?.length) setStatuses(res.data.statuses);
    }).catch(() => {});
  }, [api]);

  // PhaseB — Charge la liste des magasins (actifs) pour le filtre chips.
  useEffect(() => {
    api.get('/magasins').then(res => {
      const raw = Array.isArray(res.data) ? res.data : (res.data?.magasins || []);
      const list = raw.filter(m => m.is_active !== false);
      setMagasinsList(list);
    }).catch(() => setMagasinsList([]));
  }, [api]);

  const allowedStatusesFor = (currentStatus) => getAllowedStatuses(currentStatus, statuses, isAdmin);

  const getTimelineEvents = (device) => {
    if (device?.timeline?.length) return device.timeline;
    if (device?.created_at) {
      return [{
        label: t('rep.created'),
        status: device.status || device.statut || 'créé',
        timestamp: device.created_at
      }];
    }
    return [];
  };

  const handleOpenChat = (device) => {
    setChatContext({ relatedType: 'device', relatedId: device.ref, title: t('rep.chatTitle', { ref: device.ref }) });
  };

  const handleOpenTimeline = (device) => {
    setTimelineDevice(device);
  };

  const filteredDevices = useMemo(() => devices.filter((d) => {
    if (magasinFilter && (d.magasin_id || '') !== magasinFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      d.ref?.toLowerCase().includes(q) ||
      d.client_name?.toLowerCase().includes(q) ||
      d.client_phone?.includes(q) ||
      d.model?.toLowerCase().includes(q)
    );
  }), [devices, magasinFilter, searchQuery]);

  useEffect(() => {
    if (loading) return;
    if (!filteredDevices.length) {
      setSelectedListRef(null);
      return;
    }
    if (!filteredDevices.some((d) => d.ref === selectedListRef)) {
      setSelectedListRef(filteredDevices[0].ref);
    }
  }, [loading, filteredDevices, selectedListRef]);

  const handleSelectListRef = (ref) => {
    setSelectedListRef(ref);
    if (typeof window !== 'undefined' && window.innerWidth < 1280) {
      setMobileDetailOpen(true);
    }
  };

  const toggleBulkRef = (ref) => {
    if (!ref) return;
    setBulkSelectedRefs((prev) => (
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
    ));
  };

  const selectAllVisible = () => {
    setBulkSelectedRefs((prev) => {
      const next = new Set(prev);
      filteredDevices.forEach((d) => { if (d.ref) next.add(d.ref); });
      return Array.from(next);
    });
  };

  const selectedListDevice = filteredDevices.find((d) => d.ref === selectedListRef) || null;

  const panelHandlers = {
    onStatusClick: (device) => setStatusModalDevice(device),
    onAcomptes: fetchAcomptes,
    onAddAcompte: (device) => { setSelectedDevice(device); setShowAcompteModal(true); },
    onOpenPrint: handleOpenPrint,
    onTimeline: handleOpenTimeline,
    onNotes: setNotesDeviceRef,
    onChat: handleOpenChat,
    onCreateDevis: setCreateDevisDevice,
    onDelete: handleDelete,
  };

  const onlinePanel = (
    <div className="rounded-xl border border-white/10 bg-[#151820] p-4 mb-3" data-testid="online-requests-panel">
      <h3 className="text-sm font-bold text-gray-300 mb-4 flex items-center gap-2 uppercase tracking-wide">
        <Globe className="w-4 h-4 text-red-400" />
        {t('rep.onlineRequests', { count: onlineRequests.length })}
      </h3>
      {onlineRequests.length === 0 ? (
        <p className="text-gray-500 text-sm py-4 text-center">{t('rep.noOnlineRequests')}</p>
      ) : (
        <div className="space-y-3">
          {onlineRequests.map((req) => (
            <div key={req.ref} className="rounded-xl p-4 border border-white/10 bg-black/20" data-testid={`online-request-${req.ref}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-red-400 font-bold">{req.ref}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${req.diagnostic_paid ? 'bg-emerald-900/40 text-emerald-300' : 'bg-amber-900/40 text-amber-200'}`}>
                      {req.diagnostic_paid ? t('rep.diagnosticPaid') : t('rep.diagnosticRemaining', { amount: formatCurrency(req.reste_diagnostic || 0) })}
                    </span>
                  </div>
                  <p className="text-white font-semibold">{req.client_name}</p>
                  <p className="text-sm text-gray-400">{req.problem}</p>
                </div>
                <button
                  type="button"
                  onClick={() => confirmArrival(req.ref)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-bold whitespace-nowrap flex items-center gap-2"
                  data-testid={`confirm-arrival-${req.ref}`}
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('rep.accept')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const statusManagerPanel = (
    <div className="rounded-xl border border-white/10 bg-[#151820] p-4 mb-3" data-testid="status-manager-panel">
      <h3 className="font-bold text-sm text-gray-300 mb-3 flex items-center gap-2">
        <Settings className="w-4 h-4" /> {t('rep.statusManagement')}
      </h3>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={newStatusName}
          onChange={(e) => setNewStatusName(e.target.value)}
          className="flex-1 text-sm rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-white"
          placeholder={t('rep.newStatus')}
          data-testid="new-status-input"
        />
        <button type="button" onClick={handleAddStatus} className="px-3 py-2 rounded-lg bg-emerald-900/40 text-emerald-300 text-sm font-semibold" data-testid="add-status-btn">
          {t('common.add')}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {statuses.map((s) => {
          const isDefault = ['Reçu', 'En Cours', 'Attente Pièces', 'Terminé', 'Livré', 'Non Réparable', 'En attente de dépôt', 'Refusé'].includes(s);
          return (
            <span key={s} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${isDefault ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-white/10 text-gray-300 border border-white/15'}`}>
              {statusLabel(s)}
              {!isDefault && (
                <button type="button" onClick={() => handleDeleteStatus(s)} className="text-red-400 hover:text-red-300" title={t('common.delete')}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <Layout>
      <RepairViewErrorBoundary>
      <ReparationsAtelierView
        api={api}
        t={t}
        loading={loading}
        devices={filteredDevices}
        allDevices={devices}
        onlineCount={onlineRequests.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        magasinFilter={magasinFilter}
        onMagasinFilter={setMagasinFilter}
        magasinsList={magasinsList}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        statuses={statuses}
        selectedRef={selectedListRef}
        onSelectRef={handleSelectListRef}
        bulkSelectedRefs={bulkSelectedRefs}
        onToggleBulkRef={toggleBulkRef}
        onSelectAllVisible={selectAllVisible}
        onClearBulk={() => setBulkSelectedRefs([])}
        onBulkNote={() => setShowBulkNoteModal(true)}
        isAdmin={isAdmin}
        showOnlineRequests={showOnlineRequests}
        onToggleOnlineRequests={() => setShowOnlineRequests(!showOnlineRequests)}
        showStatusManager={showStatusManager}
        onToggleStatusManager={() => setShowStatusManager(!showStatusManager)}
        onQrSearch={() => setShowQrSearch(true)}
        onlinePanel={onlinePanel}
        statusManagerPanel={statusManagerPanel}
        {...panelHandlers}
      />

      <ModalPortal isOpen={mobileDetailOpen && !!selectedListDevice}>
        <div className="modal-dark w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="modal-dark-header flex items-center justify-between sticky top-0 bg-[#151820] z-10">
            <h3 className="font-bold">{selectedListDevice?.ref}</h3>
            <button type="button" onClick={() => setMobileDetailOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
          </div>
          <div className="p-4">
            <ReparationsDetailPanel device={selectedListDevice} api={api} statuses={statuses} isAdmin={isAdmin} {...panelHandlers} />
          </div>
        </div>
      </ModalPortal>

      {/* legacy anchor for mobile cards test id */}
      <div className="hidden" data-testid="repairs-mobile-cards" />

        {/* Acompte Modal */}
        <ModalPortal isOpen={showAcompteModal && !!selectedDevice}>
            <div className="modal-dark w-full max-w-md">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg">{t('rep.addDeposit')}</h3>
                <button onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-3 rounded-xl border" style={{ background: 'rgba(var(--theme-primary-rgb), 0.1)', borderColor: 'rgba(var(--theme-primary-rgb), 0.3)' }}>
                  <div className="text-sm text-gray-400">{t('rep.repair')}</div>
                  <div className="font-bold">{selectedDevice?.ref} - {selectedDevice?.model}</div>
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('rep.depositAmountEuro')}</label>
                  <input
                    type="number"
                    value={acompteData.montant}
                    onChange={(e) => setAcompteData({ ...acompteData, montant: parseFloat(e.target.value) || 0 })}
                    className="input-dark w-full py-3"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-300 mb-2">{t('rep.paymentMode')}</label>
                  <select
                    value={acompteData.mode_paiement}
                    onChange={(e) => setAcompteData({ ...acompteData, mode_paiement: e.target.value })}
                    className="input-dark w-full py-3"
                  >
                    <option value="Espèces">{t('rep.payCash')}</option>
                    <option value="CB">{t('rep.payCard')}</option>
                    <option value="Virement">{t('rep.payTransfer')}</option>
                    <option value="Chèque">{t('rep.payCheck')}</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAcompteModal(false)}
                    className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleAddAcompte}
                    className="flex-1 py-3 rounded-xl btn-accent font-bold"
                  >
                    {t('common.add')}
                  </button>
                </div>
              </div>
            </div>
        </ModalPortal>

        {/* Acomptes List Modal */}
        <ModalPortal isOpen={showAcomptesListModal}>
            <div className="modal-dark w-full max-w-lg">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg">{t('rep.depositHistory')}</h3>
                <button onClick={() => setShowAcomptesListModal(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              <div className="p-6">
                {acomptesList.length === 0 ? (
                  <p className="text-gray-400 text-center">{t('rep.noDeposits')}</p>
                ) : (
                  <div className="space-y-3">
                    {acomptesList.map((a) => (
                      <div key={a.id} className="flex justify-between items-center p-3 rounded-xl bg-white/5 border border-white/10">
                        <div>
                          <div className="font-bold text-green-400">
                            {formatCurrency(a.montant)}
                          </div>
                          <div className="text-sm text-gray-400">
                            {payModeLabel(a.mode_paiement)} - {formatDate(a.date_versement)}
                          </div>
                          <div className="text-xs text-gray-500">👤 {a.user_nom}</div>
                        </div>
                        <button
                          onClick={() => handlePrintAcompte(a.id, a.ref_acompte || a.id)}
                          className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold"
                          data-testid={`print-acompte-ticket-${a.id}`}
                        >
                          🖨️ {t('rep.ticket')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
        </ModalPortal>

        <ModalPortal isOpen={!!timelineDevice}>
            <div className="modal-dark w-full max-w-lg" data-testid="repair-timeline-modal">
              <div className="modal-dark-header flex items-center justify-between">
                <h3 className="font-bold text-lg">{t('rep.timeline')}</h3>
                <button onClick={() => setTimelineDevice(null)} className="text-gray-400 hover:text-white" data-testid="repair-timeline-close">✕</button>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {timelineDevice && getTimelineEvents(timelineDevice).map((event, idx) => (
                    <div key={`timeline-${idx}`} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                      <span className="mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                      <div>
                        <div className="font-semibold text-white">{event.label}</div>
                        <div className="text-xs text-gray-400">
                          {event.timestamp ? formatDate(event.timestamp, { dateStyle: 'short', timeStyle: 'short' }) : ''}
                        </div>
                        {event.actor && <div className="text-xs text-gray-500">{event.actor}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
        </ModalPortal>

        {/* Status Change Modal */}
        <ModalPortal isOpen={!!statusModalDevice}>
          <div className="modal-dark w-full max-w-sm" data-testid="status-change-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">{t('rep.changeStatus')}</h3>
              <button onClick={() => setStatusModalDevice(null)} className="text-gray-400 hover:text-white" data-testid="status-change-close">✕</button>
            </div>
            <div className="p-5">
              {statusModalDevice && (
                <>
                  <div className="text-sm text-gray-400 mb-1">{statusModalDevice.ref} - {statusModalDevice.model}</div>
                  <div className="text-xs text-gray-500 mb-4">{t('rep.currentStatus')} <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${repairStatusBadge(statusModalDevice.status).className}`}>{statusLabel(statusModalDevice.status)}</span></div>
                  <div className="space-y-2">
                    {allowedStatusesFor(statusModalDevice.status).map((s) => (
                      <button
                        key={s}
                        onClick={async () => {
                          await handleStatusChange(statusModalDevice.ref, s);
                          setStatusModalDevice(null);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-xl border font-semibold transition-all hover:brightness-125 ${repairStatusBadge(s).className}`}
                        data-testid={`status-option-${s}`}
                      >
                        {statusLabel(s)}
                      </button>
                    ))}
                  </div>
                  {allowedStatusesFor(statusModalDevice.status).length === 0 && (
                    <p className="text-center text-gray-500 text-sm py-4">{t('rep.adminOnlyStatus')}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </ModalPortal>

        {/* Notes Modal */}
        <ModalPortal isOpen={!!notesDeviceRef}>
          <div className="modal-dark w-full max-w-2xl" data-testid="repair-notes-modal">
            <RepairNotesPanel
              deviceRef={notesDeviceRef}
              onClose={() => setNotesDeviceRef(null)}
            />
          </div>
        </ModalPortal>

        <RepairBulkNoteModal
          open={showBulkNoteModal && bulkSelectedRefs.length > 0}
          devices={devices.filter((d) => bulkSelectedRefs.includes(d.ref))}
          api={api}
          onClose={() => setShowBulkNoteModal(false)}
          onSent={({ sent, skipped }) => {
            const skipN = Array.isArray(skipped) ? skipped.length : 0;
            const skipHint = skipN
              ? ' ' + t(skipN > 1 ? 'rep.bulkNoteSkippedPlural' : 'rep.bulkNoteSkippedSingular', { count: skipN })
              : '';
            alert(t(sent > 1 ? 'rep.bulkNoteSentPlural' : 'rep.bulkNoteSentSingular', { count: sent, skip: skipHint }));
            setShowBulkNoteModal(false);
            setBulkSelectedRefs([]);
          }}
        />

        <ChatModal
          isOpen={!!chatContext}
          onClose={() => setChatContext(null)}
          relatedType={chatContext?.relatedType}
          relatedId={chatContext?.relatedId}
          title={chatContext?.title}
        />

        {/* Phase 2.1 - PostSaveRepairModal */}
        <PostSaveRepairModal
          open={postSaveModal.open}
          deviceRef={postSaveModal.ref}
          defaultMode={postSaveModal.mode}
          onClose={() => setPostSaveModal({ open: false, ref: null, mode: 'ticket' })}
        />

        {/* QR Code Modal */}
        {qrDevice && (
          <ModalPortal isOpen={!!qrDevice}>
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setQrDevice(null)}>
              <div className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-sm p-6 text-center" onClick={e => e.stopPropagation()} data-testid="qr-code-modal">
                <h3 className="text-base font-bold text-white mb-1">{t('rep.qrTrackingTitle')}</h3>
                <p className="text-xs text-gray-400 mb-4">{t('rep.reference')} <span className="font-mono font-bold text-white">{qrDevice.ref}</span></p>
                <div className="bg-white rounded-xl p-4 inline-block mb-4" data-testid="qr-code-image">
                  <QRCode value={buildTrackUrl(qrDevice.ref)} size={192} level="M" />
                </div>
                <p className="text-[10px] text-gray-500 mb-4">{t('rep.qrTrackingHint')}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const svg = document.querySelector('[data-testid="qr-code-image"] svg');
                      if (!svg) return;
                      const svgData = new XMLSerializer().serializeToString(svg);
                      const printWin = window.open('', '_blank');
                      printWin.document.write(`<html><head><title>QR ${qrDevice.ref}</title><style>body{text-align:center;font-family:Arial;padding:40px}h2{margin-bottom:5px}p{color:#666;font-size:14px}svg{width:250px;height:250px;margin:20px auto;display:block}</style></head><body><h2>${qrDevice.ref}</h2><p>${qrDevice.marque || ''} ${qrDevice.modele || ''}</p>${svgData}<p style="font-size:11px;margin-top:20px">${t('rep.qrPrintHint')}</p><script>setTimeout(()=>{window.print();window.close()},500)<\/script></body></html>`);
                      printWin.document.close();
                    }}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-600/20 text-red-300 hover:bg-red-600/30 transition-all border border-red-500/30"
                    data-testid="qr-print-btn"
                  >
                    <Printer className="w-4 h-4 inline mr-1" /> {t('common.print')}
                  </button>
                  <button
                    onClick={() => setQrDevice(null)}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-white/5 text-gray-400 hover:bg-white/10 transition-all"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          </ModalPortal>
        )}

        {/* QR Search Modal */}
        {showQrSearch && (
          <ModalPortal isOpen={showQrSearch}>
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setShowQrSearch(false); setQrSearchRef(''); }}>
              <div className="bg-[#151820] border border-white/10 rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="qr-search-modal">
                <h3 className="text-base font-bold text-white mb-1 flex items-center gap-2">
                  <ScanLine className="w-5 h-5 text-red-400" />
                  {t('rep.searchRepair')}
                </h3>
                <p className="text-xs text-gray-500 mb-4">{t('rep.scanOrEnterRef')}</p>

                {/* Camera scan button */}
                <button
                  onClick={() => { setShowQrSearch(false); setShowCameraScanner(true); }}
                  className="w-full py-4 rounded-xl border border-red-500/30 bg-red-950/20 text-red-300 hover:bg-red-950/30 transition-all flex items-center justify-center gap-3 mb-4"
                  data-testid="qr-open-camera-btn"
                >
                  <Camera className="w-6 h-6" />
                  <div className="text-left">
                    <span className="text-sm font-semibold block text-white">{t('rep.scanWithCamera')}</span>
                    <span className="text-[10px] text-gray-500">{t('rep.pointAtTicketQr')}</span>
                  </div>
                </button>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-[10px] text-gray-600 uppercase tracking-wider">{t('rep.or')}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Manual ref input */}
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const ref = qrSearchRef.trim().toUpperCase();
                  if (ref) {
                    setSearchQuery(ref);
                    setShowQrSearch(false);
                    setQrSearchRef('');
                  }
                }}>
                  <input
                    type="text"
                    value={qrSearchRef}
                    onChange={(e) => setQrSearchRef(e.target.value)}
                    placeholder={t('rep.qrSearchPlaceholder')}
                    className="w-full px-4 py-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-red-500/40 mb-3"
                    data-testid="qr-search-input"
                  />
                  <div className="flex gap-2">
                    <button type="submit" className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-500 text-white transition-all" data-testid="qr-search-submit">
                      <Search className="w-4 h-4 inline mr-1" /> {t('common.search')}
                    </button>
                    <button type="button" onClick={() => { setShowQrSearch(false); setQrSearchRef(''); }} className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-white/5 text-gray-400 hover:bg-white/10 transition-all">
                      {t('common.close')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </ModalPortal>
        )}

        {/* Camera QR Scanner */}
        {showCameraScanner && (
          <QrScanner
            onScan={(ref) => {
              setShowCameraScanner(false);
              setSearchQuery(ref.toUpperCase());
            }}
            onClose={() => setShowCameraScanner(false)}
          />
        )}

        {/* Create Devis from Repair Modal */}
        <CreateDevisFromRepairModal
          isOpen={!!createDevisDevice}
          device={createDevisDevice}
          onClose={() => setCreateDevisDevice(null)}
          onCreated={() => { setCreateDevisDevice(null); fetchDevices(); }}
        />
      </RepairViewErrorBoundary>
    </Layout>
  );
};

export default ReparationsPage;
